import React, { useEffect, useMemo, useState } from "react";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useNavigate } from "react-router-dom";
import AdminCrossBoardService from "../../pzl_cross/board/AdminCrossBoardService";
import AdminCrossService from "../../pzl_cross/tpl/AdminCrossService";
import AdminGridBoardService from "../../pzl_grid/board/AdminGridBoardService";
import AdminGridService from "../../pzl_grid/tpl/AdminGridService";
import Logger from "../../../services/Logger";
import AdminStoryService from "../../story/AdminStoryService";
import { getLocalizedStoryFromTemplate } from "../getEnStoryFromTemplate";

function tplHasStory(title, text, imageUrl) {
    const t = typeof title === "string" ? title.trim() : "";
    const x = typeof text === "string" ? text.trim() : "";
    const img = typeof imageUrl === "string" ? imageUrl.trim() : "";
    return Boolean(t || x || img);
}

/** Story illustration URLs on template `story` (camelCase + snake_case). */
function readStoryImageFields(puzzle) {
    const story = puzzle?.story && typeof puzzle.story === "object" ? puzzle.story : {};
    const pick = (...keys) => {
        for (const k of keys) {
            const v = story[k];
            if (typeof v === "string" && v.trim()) return v.trim();
        }
        return "";
    };
    return {
        main: pick("imageUrl", "image_url"),
        preview: pick("imagePreviewUrl", "image_preview_url"),
        icon400: pick("imageIcon400Url", "image_icon_400_url"),
        icon200: pick("imageIcon200Url", "image_icon_200_url"),
    };
}

/** True when preview / icon derivatives are missing (server can fill via POST gen-image/{tplId}). */
function storyNeedsDerivedImages(puzzle) {
    const imgs = readStoryImageFields(puzzle);
    if (!imgs.main) return false;
    return !imgs.preview || !imgs.icon400 || !imgs.icon200;
}

/** No illustration row at all (main + derived), but story slot may have text. */
function storyHasNoImages(puzzle) {
    const imgs = readStoryImageFields(puzzle);
    return !imgs.main && !imgs.preview && !imgs.icon400 && !imgs.icon200;
}

function readGenError(e) {
    return e?.response?.message || e?.message || "Generation failed.";
}

function isClaudeRefusalError(message) {
    if (!message || typeof message !== "string") return false;
    const m = message.toLowerCase();
    return m.includes("refus") || m.includes("stop_reason=refusal");
}

function parseFilledCoordinates(puzzle) {
    if (!puzzle?.filledCoordinates) return [];
    try {
        const raw = JSON.parse(puzzle.filledCoordinates);
        return Array.isArray(raw) ? raw : [];
    } catch {
        return [];
    }
}

/** Same rules as crossword template detail: reversed cells use reversed text as displayToken. */
function wordsFromFilledCoordinates(puzzle) {
    return parseFilledCoordinates(puzzle)
        .filter((wordObj) => wordObj?.text !== null && wordObj?.text !== undefined && String(wordObj.text).trim())
        .map((wordObj) => {
            const t = String(wordObj.text);
            const displayText = wordObj.reversed ? t.split("").reverse().join("") : t;
            return {
                ...wordObj,
                displayText,
            };
        });
}

function puzzleWordAliasSet(wordObjs) {
    const s = new Set();
    for (const w of wordObjs) {
        if (w.text != null) s.add(String(w.text).trim().toLowerCase());
        if (w.displayText) s.add(String(w.displayText).trim().toLowerCase());
    }
    return s;
}

/** Inline story body: optionally replace puzzle words with one gray tile per letter. */
function StoryTextWithHiddenPuzzleWords({ text, wordObjs, hideWords }) {
    const masked = Boolean(hideWords && text && wordObjs?.length > 0);
    const fragments = useMemo(() => {
        if (!masked) return null;
        const keys = puzzleWordAliasSet(wordObjs);
        const parts = String(text).split(/(\b[\w']+\b)/g);
        return parts.map((part, idx) => {
            if (/^[\w']+$/.test(part) && keys.has(part.toLowerCase())) {
                const suffix = `${idx}:${part}`;
                return (
                    <Box
                        key={`h-${suffix}`}
                        component="span"
                        sx={{
                            display: "inline-flex",
                            gap: "2px",
                            alignItems: "center",
                            verticalAlign: "text-bottom",
                            mx: "1px",
                        }}
                    >
                        {Array.from({ length: part.length }, (_, i) => (
                            <Box
                                component="span"
                                key={`${suffix}-${i}`}
                                sx={{
                                    width: "0.62em",
                                    height: "1.15em",
                                    minWidth: "7px",
                                    bgcolor: "grey.500",
                                    borderRadius: "2px",
                                    display: "inline-block",
                                    flexShrink: 0,
                                    boxSizing: "border-box",
                                }}
                            />
                        ))}
                    </Box>
                );
            }
            return part;
        });
    }, [masked, text, wordObjs]);

    return (
        <Typography
            component="div"
            variant="body1"
            sx={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                lineHeight: 1.7,
            }}
        >
            {masked ? fragments : text}
        </Typography>
    );
}

/**
 * Opens a dialog with crossword or grid template story (English or French by template language).
 * When empty, offers "Generate it" (gen-story). When story exists but preview/icons are missing (or no illustration URLs),
 * offers "Generate Image" (POST /v1/story/gen-image/{tplId}).
 *
 * Grid vs cross is inferred from `puzzle.type` (WORD_SEARCH / HIDDEN_MESSAGE → grid-tpl APIs).
 *
 * @param {{ id: string, type?: string }} puzzle — template row from admin board
 * @param {() => void} [onRefresh] — refetch puzzle in parent after successful generation
 */
function StoryImageDerivativesRow({ storyImgFields }) {
    const slots = [
        { key: "preview", label: "Preview", hint: "1200×630", url: storyImgFields.preview },
        { key: "400", label: "400", hint: "Icon", url: storyImgFields.icon400 },
        { key: "200", label: "200", hint: "Icon", url: storyImgFields.icon200 },
    ];
    return (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
            {slots.map(({ key, label, hint, url }) => (
                <Box key={key} sx={{ flex: "1 1 96px", minWidth: 96, maxWidth: 152 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5, flexWrap: "wrap" }}>
                        <ImageOutlinedIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                        <Typography component="span" variant="caption" fontWeight={600} color="text.secondary">
                            {label}
                        </Typography>
                        <Typography component="span" variant="caption" color="text.disabled">
                            ({hint})
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            height: 76,
                            borderRadius: 1,
                            border: "1px solid",
                            borderColor: "divider",
                            bgcolor: "grey.50",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: "hidden",
                        }}
                    >
                        {url ? (
                            <Box
                                component="img"
                                src={url}
                                alt=""
                                sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", display: "block" }}
                            />
                        ) : (
                            <Typography variant="caption" color="text.disabled" sx={{ px: 1, textAlign: "center" }}>
                                Missing
                            </Typography>
                        )}
                    </Box>
                </Box>
            ))}
        </Box>
    );
}

/** Some board payloads nest narrative under `template.story` instead of root `story`. */
function puzzleWithMergedStory(base) {
    if (!base || typeof base !== "object") return base;
    if (base.story && typeof base.story === "object") return base;
    const tplStory = base.template?.story;
    if (tplStory && typeof tplStory === "object") {
        return { ...base, story: tplStory };
    }
    return base;
}

export function AdminStoryPreviewTrigger({ puzzle, onRefresh }) {
    const [open, setOpen] = useState(false);
    const [freshTpl, setFreshTpl] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [generatingImage, setGeneratingImage] = useState(false);
    const [refetching, setRefetching] = useState(false);
    const [genError, setGenError] = useState(null);
    const [triedGenerate, setTriedGenerate] = useState(false);
    const [hidePuzzleWords, setHidePuzzleWords] = useState(false);
    const navigate = useNavigate();

    const effectivePuzzle = useMemo(
        () => puzzleWithMergedStory(freshTpl ?? puzzle),
        [freshTpl, puzzle]
    );

    const isGridTpl =
        puzzle?.type === "WORD_SEARCH" || puzzle?.type === "HIDDEN_MESSAGE";
    const { title, text, imageUrl } = useMemo(
        () => getLocalizedStoryFromTemplate(effectivePuzzle),
        [effectivePuzzle]
    );
    const storyImgFields = useMemo(() => readStoryImageFields(effectivePuzzle), [effectivePuzzle]);
    const displayImageUrl = imageUrl || storyImgFields.main;
    const hasStory = tplHasStory(title, text, displayImageUrl);

    const showGenerateImageButton =
        Boolean(puzzle?.id) &&
        hasStory &&
        (storyHasNoImages(effectivePuzzle) || storyNeedsDerivedImages(effectivePuzzle));

    const puzzleWordObjs = useMemo(
        () => wordsFromFilledCoordinates(effectivePuzzle),
        [effectivePuzzle]
    );
    const canHidePuzzleWords = puzzleWordObjs.length > 0;

    useEffect(() => {
        setFreshTpl(null);
    }, [puzzle?.id]);

    const closeDialog = () => {
        setFreshTpl(null);
        setTriedGenerate(false);
        setGenError(null);
        setHidePuzzleWords(false);
        setOpen(false);
    };

    const busy = generating || generatingImage || refetching;

    const handleOpen = () => {
        setGenError(null);
        setTriedGenerate(false);
        setFreshTpl(null);
        setHidePuzzleWords(false);
        setOpen(true);
    };

    const fetchLatestTemplate = async (id) => {
        const updated = isGridTpl
            ? await AdminGridBoardService.requestHwTpl(id)
            : await AdminCrossBoardService.requestCrossTpl(id);
        setFreshTpl(updated);
        return updated;
    };

    const handleRefreshTemplate = async () => {
        const id = puzzle?.id;
        if (!id) return;
        setGenError(null);
        setRefetching(true);
        try {
            await fetchLatestTemplate(id);
        } catch (e) {
            Logger.error("AdminStoryPreviewTrigger refresh template:", e);
            setGenError(readGenError(e));
        } finally {
            setRefetching(false);
        }
    };

    const handleGenerate = async () => {
        const id = puzzle?.id;
        if (!id) return;
        setGenError(null);
        setGenerating(true);
        try {
            if (isGridTpl) {
                await AdminGridService.genStory(id, {});
            } else {
                await AdminCrossService.genStory(id, {});
            }
            if (typeof onRefresh === "function") {
                onRefresh();
            }
            try {
                await fetchLatestTemplate(id);
            } catch (fetchErr) {
                Logger.error("AdminStoryPreviewTrigger refetch after gen:", fetchErr);
                setGenError(readGenError(fetchErr));
            }
            setTriedGenerate(true);
        } catch (e) {
            Logger.error("AdminStoryPreviewTrigger generate:", e);
            setGenError(readGenError(e));
        } finally {
            setGenerating(false);
        }
    };

    const handleGenerateSafeRetry = async () => {
        const id = puzzle?.id;
        if (!id) return;
        setGenerating(true);
        try {
            if (isGridTpl) {
                await AdminGridService.genStorySafe(id, {});
            } else {
                await AdminCrossService.genStorySafe(id, {});
            }
            setGenError(null);
            if (typeof onRefresh === "function") {
                onRefresh();
            }
            try {
                await fetchLatestTemplate(id);
            } catch (fetchErr) {
                Logger.error("AdminStoryPreviewTrigger refetch after gen-story-safe:", fetchErr);
                setGenError(readGenError(fetchErr));
            }
            setTriedGenerate(true);
        } catch (e) {
            Logger.error("AdminStoryPreviewTrigger generate safe:", e);
            setGenError(readGenError(e));
        } finally {
            setGenerating(false);
        }
    };

    const handleGenerateImage = async () => {
        const id = puzzle?.id;
        if (!id) return;
        setGenError(null);
        setGeneratingImage(true);
        try {
            const result = await AdminStoryService.genImageForTemplate(id);
            if (!result.success) {
                setGenError(result.error || "Generate image failed");
                return;
            }
            if (typeof onRefresh === "function") {
                onRefresh();
            }
            try {
                await fetchLatestTemplate(id);
            } catch (fetchErr) {
                Logger.error("AdminStoryPreviewTrigger refetch after gen-image:", fetchErr);
                setGenError(readGenError(fetchErr));
            }
        } catch (e) {
            Logger.error("AdminStoryPreviewTrigger gen-image:", e);
            setGenError(readGenError(e));
        } finally {
            setGeneratingImage(false);
        }
    };

    if (!puzzle?.id) return null;

    const storyId = effectivePuzzle?.story?.id;

    return (
        <>
            <Tooltip title={hasStory ? "Story — title, image, text" : "No story"} arrow>
                <IconButton
                    onClick={handleOpen}
                    color={hasStory ? "default" : "warning"}
                    sx={{
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                        borderRadius: "50%",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        flexShrink: 0,
                        "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 1)",
                        },
                    }}
                >
                    <MenuBookRoundedIcon />
                </IconButton>
            </Tooltip>
            {storyId && (
                <Tooltip title="Open story admin page" arrow>
                    <IconButton
                        onClick={() => navigate(`/admin/story/${storyId}`)}
                        sx={{
                            backgroundColor: "rgba(255, 255, 255, 0.9)",
                            borderRadius: "50%",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                            flexShrink: 0,
                            "&:hover": {
                                backgroundColor: "rgba(255, 255, 255, 1)",
                            },
                        }}
                    >
                        <OpenInNewIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}
            <Dialog
                open={open}
                onClose={() => {
                    if (!busy) closeDialog();
                }}
                disableEscapeKeyDown={busy}
                maxWidth="sm"
                fullWidth
                scroll="paper"
            >
                <DialogTitle sx={{ pr: 6 }}>{title || "Story"}</DialogTitle>
                <DialogContent dividers sx={{ pt: displayImageUrl || !hasStory ? 2 : undefined }}>
                    {genError ? (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {genError}
                            {isClaudeRefusalError(genError) ? (
                                <Typography variant="caption" display="block" sx={{ mt: 0.75, opacity: 0.9 }}>
                                    Claude declined this word bank. Use <strong>Retry</strong> below for a softer
                                    educational prompt that skips high-risk words.
                                </Typography>
                            ) : (
                                <Typography variant="caption" display="block" sx={{ mt: 0.75, opacity: 0.9 }}>
                                    Use <strong>Retry</strong> below to try again with a softer prompt.
                                </Typography>
                            )}
                        </Alert>
                    ) : null}
                    {hasStory && displayImageUrl ? (
                        <Box
                            component="img"
                            src={displayImageUrl}
                            alt={title || "Story illustration"}
                            sx={{
                                width: "100%",
                                maxHeight: 280,
                                objectFit: "contain",
                                borderRadius: 1,
                                mb: 2,
                                bgcolor: "grey.100",
                                display: "block",
                            }}
                        />
                    ) : null}
                    <StoryImageDerivativesRow storyImgFields={storyImgFields} />
                    {!hasStory ? (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "flex-start" }}>
                            <Typography color="text.secondary">
                                No story on this template yet. Use <strong>Generate story</strong> below.
                            </Typography>
                            {triedGenerate && !generating ? (
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignItems: "flex-start" }}>
                                    <Typography variant="caption" color="text.secondary">
                                        If the server is still writing the story, wait a moment and load again.
                                    </Typography>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={handleRefreshTemplate}
                                        disabled={refetching}
                                        startIcon={refetching ? <CircularProgress size={16} color="inherit" /> : null}
                                    >
                                        {refetching ? "Loading…" : "Refresh"}
                                    </Button>
                                </Box>
                            ) : null}
                        </Box>
                    ) : (
                        <>
                            {showGenerateImageButton ? (
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: text ? 2 : 0 }}>
                                    Main illustration present but preview or icon URLs are missing — use{" "}
                                    <strong>Generate Image</strong> in the bar below.
                                </Typography>
                            ) : null}
                            {text ? (
                                <StoryTextWithHiddenPuzzleWords
                                    text={text}
                                    wordObjs={puzzleWordObjs}
                                    hideWords={hidePuzzleWords && canHidePuzzleWords}
                                />
                            ) : !displayImageUrl ? (
                                <Typography color="text.secondary">No story text.</Typography>
                            ) : null}
                        </>
                    )}
                </DialogContent>
                <DialogActions
                    sx={{
                        display: "flex",
                        width: "100%",
                        px: 2,
                        py: 1.5,
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: 1,
                        boxSizing: "border-box",
                    }}
                >
                    {hasStory && text && canHidePuzzleWords ? (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={hidePuzzleWords}
                                    onChange={(e) => setHidePuzzleWords(e.target.checked)}
                                    disabled={busy}
                                />
                            }
                            label="Hide words in puzzle"
                            sx={{ mr: 0, ml: 0, flexShrink: 0 }}
                        />
                    ) : null}
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleGenerate}
                        disabled={busy}
                        startIcon={generating ? <CircularProgress size={18} color="inherit" /> : null}
                        sx={{ flexShrink: 0, textTransform: "none" }}
                    >
                        {generating ? "Generating…" : "Generate story"}
                    </Button>
                    {genError ? (
                        <Button
                            variant="contained"
                            color="warning"
                            onClick={handleGenerateSafeRetry}
                            disabled={busy}
                            startIcon={generating ? <CircularProgress size={18} color="inherit" /> : null}
                            sx={{ flexShrink: 0, textTransform: "none" }}
                        >
                            {generating ? "Retrying…" : "Retry"}
                        </Button>
                    ) : null}
                    {showGenerateImageButton ? (
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={handleGenerateImage}
                            disabled={busy}
                            startIcon={
                                generatingImage ? (
                                    <CircularProgress size={18} color="inherit" />
                                ) : (
                                    <ImageOutlinedIcon />
                                )
                            }
                            sx={{ flexShrink: 0, textTransform: "none" }}
                        >
                            {generatingImage ? "Generating…" : "Generate Image"}
                        </Button>
                    ) : null}
                    <Box sx={{ flexGrow: 1, minWidth: 8 }} />
                    <Button
                        onClick={closeDialog}
                        variant="contained"
                        disabled={busy}
                        sx={{ flexShrink: 0 }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
